// @flow
import { createSelector } from 'reselect';
import type {
  BackupConfiguration,
  BackupRestoreStatus,
  BrandingSettings,
  Catalog,
  CatalogingOptions,
  CatalogProperties,
  CatalogType,
  CertInfo,
  CertRequestData,
  DirectoryServiceDescription,
  DirSvcMapping,
  DirSvcNode,
  DiskPreviewInfo,
  EmailServerConfiguration,
  ExclusionInfo,
  FieldDefinition,
  IngestConfig,
  License,
  LicensedFeatures,
  LoggingConfig,
  MediaEngine,
  MetadataDefinition,
  MetadataMapping,
  NamespaceDefinition,
  NetPublishSite,
  ProductVersion,
  ReachoutMessage,
  ServerFolder,
  ServerStatus,
  ServerStatusErrorMessage,
  Role,
  SmartKeywordsSettings,
  State,
  System,
  User,
  UserCatalogInfo,
} from './types';

import {
  ASSET_CAP_QUANTUM,
  MAX_NUM_ASSETS,
  MAX_NUM_ASSETS_STUDIO,
  MAX_NUM_MEDIAENGINES,
  MAX_NUM_MEDIAENGINES_STUDIO,
  MAX_NUM_SEATS,
  MAX_NUM_SEATS_STUDIO,
  PRODUCT_TYPE_DEMO,
  PRODUCT_TYPE_NONE,
  PRODUCT_TYPE_SERVER,
  PRODUCT_TYPE_STUDIO,
  SN_TYPE_API,
  SN_TYPE_ASSETCAP,
  SN_TYPE_DEMO,
  SN_TYPE_MEDIAENGINE,
  SN_TYPE_NETPUBLISH,
  SN_TYPE_SERVER,
  SN_TYPE_SINGLESIGNON,
  SN_TYPE_STUDIO,
  SN_TYPE_USERCOUNT,
} from 'util/constants';

import { PLATFORM_WINDOWS } from 'portfolio-shared/util/sharedConstants';

import { fieldDefForFieldsListItemName } from 'util/fieldsPageUtils';

import { NAME_PUBLISHER } from 'util/roleUtils';

import { GLOBAL_SETTING } from 'util/serverUtils';

type booleanSelector = State => boolean;
type stringSelector = State => string;
type numberSelector = State => number;
type optStringSelector = State => ?string;

type optFieldDefinitionSelector = State => ?FieldDefinition;

//
// Intl
//
const getIntl = (state: State) => state.intl;
const getLocale: stringSelector = state => state.intl.locale || 'en';

//
// Analytics
//
const getRTIEnabled: booleanSelector = state => state.rti.rtiEnabled;

//
// Auth
//
const getIsFullAdmin: booleanSelector = state => !!state.auth.adminSessionId;
const getHasIdentityService: booleanSelector = state =>
  !!state.auth.identitySessionId;
const getIsLoggedIn: booleanSelector = state =>
  !!(state.auth.adminSessionId || state.auth.identitySessionId);
const getLastActivity: numberSelector = state => state.auth.lastActivity;
const getUsername: optStringSelector = state => state.auth.username;
const getIdentityEncryptedPassword: optStringSelector = state =>
  state.auth.identityEncryptedPassword;
const getAdminSession: optStringSelector = state => state.auth.adminSessionId;
const getIdentitySession: optStringSelector = state =>
  state.auth.identitySessionId;

//
// Server
//
const getServerStatus: State => ?ServerStatus = state =>
  state.server.serverStatus || null;

const getIsServerPlatformWindows: booleanSelector = state =>
  state.server.serverOsPlatform === PLATFORM_WINDOWS;

const getServerFolderRoot: stringSelector = state =>
  state.server.serverFolderRoot;

const getBackupConfig: State => ?BackupConfiguration = state =>
  state.server.backupConfig || null;

const getBackupRestoreStatus: State => ?BackupRestoreStatus = state =>
  state.server.backupRestoreStatus;

const getServerFolders: State => ServerFolder[] = state =>
  state.server.serverFolders;

const getPorts: State => Object = state => state.server.ports || {};

const getGlobalSettings: State => ?Object = state =>
  state.server.globalSettings;

const getLoggingConfig: State => ?LoggingConfig = state =>
  state.server.loggingConfig;

const getEmailServerConfiguration: State => ?EmailServerConfiguration = state =>
  state.server.emailServerConfiguration;

const getBrandingSettings: State => ?BrandingSettings = state =>
  state.server.brandingSettings;

const getIsEmailServerConfigPendingValidation: booleanSelector = state =>
  state.server.emailConfigValidationPending;

const getCreateAutosyncFolder: booleanSelector = state =>
  !!state.server.globalSettings &&
  state.server.globalSettings[GLOBAL_SETTING.CREATE_AUTOSYNC_FOLDER] === 'true';

// Admin Properties - errorMessages
const kStartupFailureId = 'startupFailure';

function _getStartupFailureMessage(serverStatus: ?ServerStatus): ?string {
  let message: ?string = null;
  if (serverStatus) {
    let errorMessage: ?ServerStatusErrorMessage = serverStatus.errorMessages.find(
      (em: ServerStatusErrorMessage) => em.id === kStartupFailureId
    );
    if (errorMessage) {
      message = errorMessage.parameters.reason;
    }
  }
  return message;
}

const getServerStartupFailureReason: optStringSelector = state =>
  _getStartupFailureMessage(state.server.serverStatus);

const getServerErrorStatuses: State => ServerStatusErrorMessage[] = state => {
  const serverStatus = state.server.serverStatus;
  return serverStatus
    ? serverStatus.errorMessages.filter(em => em.id !== kStartupFailureId)
    : [];
};

const getDiagnosticDataStatus: stringSelector = state =>
  state.server.diagnosticDataStatus || '';

const getServerRunState: stringSelector = state =>
  (state.server &&
    state.server.serverStatus &&
    state.server.serverStatus.runState) ||
  '';

const getProductVersions: State => ProductVersion[] = state =>
  (state.server &&
    state.server.serverStatus &&
    state.server.serverStatus.versions) ||
  [];

const getMediaEngines: State => MediaEngine[] = state =>
  (state.server && state.server.mediaEngines) || [];

const getIsMediaEngineTestPending: State => boolean = state =>
  state.server && state.server.mediaEngineTestPending;

const getTotalAssetCount: numberSelector = state =>
  (state.server &&
    state.server.globalStatistics &&
    parseInt(state.server.globalStatistics.totalAssetCount, 10)) ||
  0;
const getSeatCount: numberSelector = state =>
  (state.server &&
    state.server.globalStatistics &&
    parseInt(state.server.globalStatistics.seatCount, 10)) ||
  0;
const getSmartKeywordsRemainingCount: numberSelector = state =>
  (state.server &&
    state.server.globalStatistics &&
    parseInt(state.server.globalStatistics.smartKeywordsRemainingCount, 10)) ||
  0;

const getApiVersion: optStringSelector = createSelector(
  getProductVersions,
  (productVersions: ProductVersion[]): ?string => {
    const server = productVersions.find(
      version => version.componentName === 'Server API'
    );
    return server && server.versionNumber;
  }
);

const getIsCommunicatingWithDirectoryService: booleanSelector = state =>
  state.server.isCommunicatingWithDirectoryService;

const getDirectoryServiceDescription: State => ?DirectoryServiceDescription = state =>
  state.server && state.server.directoryServiceDescription;

//
// Directory Services
//

const getDirectoryServiceMappings: State => DirSvcMapping[] = state =>
  state.dirSvc.mappings;

// TODO: more specific type
const getDirectoryServiceQueryResults: State => Object[] = state =>
  state.dirSvc.queryResults;

const getIsDirectoryServiceQueryPending: State => boolean = state =>
  state.dirSvc.queryIsPending;

const getDirectoryServiceDestination: stringSelector = state =>
  state.dirSvc.destination;

const getDirectoryServiceQueryType: stringSelector = state =>
  state.dirSvc.queryType;

const getSelectedDirectoryServiceNode: State => ?DirSvcNode = state =>
  state.dirSvc.selectedNode;

const getActiveDirectoryServiceMapping: State => ?DirSvcMapping = createSelector(
  getDirectoryServiceMappings,
  getDirectoryServiceDestination,
  (mappings, destination) =>
    mappings && mappings.find(m => m.targetGroupGuid === destination)
);

const getIsDirectoryServiceMappingsDirty: State => boolean = createSelector(
  getDirectoryServiceMappings,
  mappings =>
    mappings.some(mapping => mapping.entries.some(entry => entry.unsaved))
);

//
// Reachout Messages
//

const getReachoutMessages: State => ?(ReachoutMessage[]) = state =>
  state.server && state.server.reachoutMessages;

const getDismissedMessageIds: State => Array<string> = state =>
  state.reachout && state.reachout.dismissedMessageIds;

//
// Licenses
//

const getLicenses: State => License[] = state =>
  (state.server && state.server.licenses) || [];

const getExtensisAccount: optStringSelector = state =>
  state.license.extensisAccount;

const getExtensisSystems: State => System[] = state =>
  state.license.extensisSystems;

const getLicenseMaintenanceEndDate: optStringSelector = state =>
  state.license.licenseMaintenanceEndDate;

const getIsPendingResponseFromBonfire: booleanSelector = state =>
  state.license.isPendingResponse;

function _getIsMainProduct(licenses: License[], productId: string): boolean {
  const matchingLicense =
    licenses && licenses.find(license => license.productId === productId);
  return !!(matchingLicense && !matchingLicense.isExpiredLicense);
}

function _getMainProductType(licenses: License[]) {
  if (_getIsMainProduct(licenses, SN_TYPE_DEMO)) {
    return PRODUCT_TYPE_DEMO;
  } else if (_getIsMainProduct(licenses, SN_TYPE_STUDIO)) {
    return PRODUCT_TYPE_STUDIO;
  } else if (_getIsMainProduct(licenses, SN_TYPE_SERVER)) {
    return PRODUCT_TYPE_SERVER;
  } else {
    return PRODUCT_TYPE_NONE;
  }
}

function _hasLicenseForProductId(
  productId: string,
  licenses: License[],
  isDemoMode: boolean,
  demoEnablesFeature: boolean
) {
  if (licenses) {
    const match = licenses.find(license => license.productId === productId);
    return (
      (match && !match.isExpiredLicense) || (demoEnablesFeature && isDemoMode)
    );
  }

  return false;
}

const getLicensedFeatures: State => LicensedFeatures = createSelector(
  getLicenses,
  getLicenseMaintenanceEndDate,
  (licenses: License[], maintenanceEndDate: ?string) => {
    const productType = _getMainProductType(licenses);
    const isDemo = productType === PRODUCT_TYPE_DEMO;
    const isServer = productType === PRODUCT_TYPE_SERVER;
    return {
      api: _hasLicenseForProductId(SN_TYPE_API, licenses, isDemo, true),
      dirSvc: isDemo || isServer,
      main:
        _hasLicenseForProductId(SN_TYPE_SERVER, licenses, isDemo, true) ||
        _hasLicenseForProductId(SN_TYPE_STUDIO, licenses, isDemo, false),
      me: _hasLicenseForProductId(SN_TYPE_MEDIAENGINE, licenses, isDemo, true),
      np: _hasLicenseForProductId(SN_TYPE_NETPUBLISH, licenses, isDemo, true),
      sk:
        !!(maintenanceEndDate && new Date(maintenanceEndDate) >= Date.now()) ||
        isDemo,
      sso: _hasLicenseForProductId(
        SN_TYPE_SINGLESIGNON,
        licenses,
        isDemo,
        true
      ),
    };
  }
);

const getMediaEngineTotalSeatCount: numberSelector = createSelector(
  getLicenses,
  (licenses: License[]): number => {
    const me = licenses.find(
      license => license.productId === SN_TYPE_MEDIAENGINE
    );
    const productType = _getMainProductType(licenses);
    return (
      (productType === PRODUCT_TYPE_DEMO && MAX_NUM_MEDIAENGINES) ||
      (productType === PRODUCT_TYPE_STUDIO && MAX_NUM_MEDIAENGINES_STUDIO) ||
      (me && me.numSeats) ||
      1 // always return internal ME
    );
  }
);

const getEnabledMediaEnginesCount: numberSelector = createSelector(
  getMediaEngines,
  (mediaEngines: MediaEngine[]) => {
    const enabled = mediaEngines.filter(mediaEngine => mediaEngine.enabled);
    return enabled.length;
  }
);

const getAssetCapCount: numberSelector = createSelector(
  getLicenses,
  (licenses: License[]) => {
    const productType = _getMainProductType(licenses);
    const ac = licenses.find(license => license.productId === SN_TYPE_ASSETCAP);
    return (
      (productType === PRODUCT_TYPE_DEMO && MAX_NUM_ASSETS) ||
      (productType === PRODUCT_TYPE_STUDIO && MAX_NUM_ASSETS_STUDIO) ||
      (ac && ac.numSeats * ASSET_CAP_QUANTUM) ||
      0
    );
  }
);

const getMaxSeatCount: numberSelector = createSelector(
  getLicenses,
  (licenses: License[]): number => {
    const productType = _getMainProductType(licenses);
    const uc = licenses.find(
      license => license.productId === SN_TYPE_USERCOUNT
    );
    return (
      (productType === PRODUCT_TYPE_DEMO && MAX_NUM_SEATS) ||
      (productType === PRODUCT_TYPE_STUDIO && MAX_NUM_SEATS_STUDIO) ||
      (uc && uc.numSeats) ||
      0
    );
  }
);

const getIsRestarting: booleanSelector = state => state.server.isRestarting;

const getIsAdminServerRestarting: booleanSelector = state =>
  state.server.isAdminServerRestarting;

const getIsSendingDiagnosticData: booleanSelector = state =>
  state.server.isSendingDiagnosticData;

// Dialog Panel
const getActivePanelId: optStringSelector = ({ dialogPanel: { activeIds } }) =>
  activeIds[activeIds.length - 1];

const getActivePanelData: State => Object = ({
  dialogPanel: { activeIds, data },
}) => data[activeIds[activeIds.length - 1]];

//
// Catalogs
//

const getCatalogs: State => Catalog[] = state => state.catalog.catalogs;
const getCatalogTypes: State => CatalogType[] = state =>
  state.catalog.catalogTypes;
const getCustomCatalogTemplates: State => CatalogType[] = state =>
  state.catalog.catalogTypes
    .filter(t => t.guid)
    .sort((a, b) => a.name.localeCompare(b.name));
const getCatalogProperties: State => CatalogProperties = state =>
  state.catalog.catalogProperties;
const getCatalogingOptions: State => ?CatalogingOptions = state =>
  state.catalog.catalogProperties &&
  state.catalog.catalogProperties.catalogingOptions;
const getUseConstrainedIngest: booleanSelector = createSelector(
  getCatalogingOptions,
  catalogingOptions =>
    !!(catalogingOptions && catalogingOptions.useConstrainedIngest)
);
const getCatalogExclusionInfo: State => ?ExclusionInfo = state =>
  state.catalog.catalogProperties &&
  state.catalog.catalogProperties.exclusionInfo;
const getDiskPreviewInfo: State => ?DiskPreviewInfo = state =>
  state.catalog.catalogProperties &&
  state.catalog.catalogProperties.diskPreviewInfo;
const getFields: State => FieldDefinition[] = state => state.catalog.fields;
const getIngestConfig: State => IngestConfig = state =>
  (state.catalog.catalogProperties &&
    state.catalog.catalogProperties.ingestConfig) || {
    headerText: '',
    fields: [],
  };
const getSmartKeywordsSettings: State => ?SmartKeywordsSettings = createSelector(
  getCatalogProperties,
  catalogProperties =>
    catalogProperties && catalogProperties.smartKeywordsSettings
);
const getMetadataDefinitions: State => MetadataDefinition[] = state =>
  state.catalog.metadataDefinitions;
const getMetadataMappings: State => MetadataMapping[] = state =>
  state.catalog.metadataMappings;
const getSelectedFieldCategory: State => ?string = state =>
  state.catalog.selectedFieldCategory;
const getSelectedFieldsListItemName: State => ?string = state =>
  state.catalog.selectedFieldsListItemName;
const getUserCatalogInfo: State => UserCatalogInfo[] = state =>
  state.catalog.userCatalogInfo;
const getSelectedField: optFieldDefinitionSelector = createSelector(
  getFields,
  getMetadataDefinitions,
  getSelectedFieldsListItemName,
  (fields, mds, name): ?FieldDefinition => {
    if (name) {
      return fieldDefForFieldsListItemName(name, mds, fields);
    }
    return null;
  }
);
const getFieldNames: State => string[] = createSelector(
  getFields,
  (fields: FieldDefinition[]) => fields.map(field => field.systemName)
);
const getCustomXMPNamespaces: State => NamespaceDefinition[] = state =>
  state.catalog.customXMPNamespaces;

//
// Roles
//

const getRoles: State => Role[] = state => state.roles.roles;
const getSelectedRole: State => ?Role = state =>
  state.roles.roles.find(role => role.uuid === state.roles.selectedRoleId);

const getDefaultRole: State => ?Role = createSelector(
  getGlobalSettings,
  getRoles,
  (globalSettings, roles) => {
    const defaultRoleName =
      globalSettings &&
      globalSettings[GLOBAL_SETTING.DEFAULT_USER_ACCESS_LEVEL];
    const foundRole =
      defaultRoleName && roles.find(r => r.name === defaultRoleName);
    return foundRole || roles.find(r => r.name === NAME_PUBLISHER);
  }
);

//
// Users
//

const getUsers: State => User[] = state => state.user.users;

//
// NetPublish
//

const getSites: State => NetPublishSite[] = state => state.np.sites;
const getNPServerStatus: State => Object = state => state.np.status;

//
// Misc View Settings
//

const getNotifications: State => Object[] = state =>
  state.globalNotifications.notifications;

//
// Certs
//

const getCertInfo: State => ?CertInfo = state => state.cert.certificate;
const getCertRequestData: State => ?CertRequestData = state =>
  state.cert.request;

export {
  getActiveDirectoryServiceMapping,
  getActivePanelData,
  getActivePanelId,
  getAdminSession,
  getApiVersion,
  getAssetCapCount,
  getBackupConfig,
  getBackupRestoreStatus,
  getBrandingSettings,
  getCatalogExclusionInfo,
  getCatalogingOptions,
  getCatalogProperties,
  getCatalogs,
  getCatalogTypes,
  getCertInfo,
  getCertRequestData,
  getCreateAutosyncFolder,
  getCustomCatalogTemplates,
  getCustomXMPNamespaces,
  getDefaultRole,
  getDiagnosticDataStatus,
  getDirectoryServiceDescription,
  getDirectoryServiceDestination,
  getDirectoryServiceMappings,
  getDirectoryServiceQueryResults,
  getDirectoryServiceQueryType,
  getDiskPreviewInfo,
  getDismissedMessageIds,
  getEmailServerConfiguration,
  getEnabledMediaEnginesCount,
  getExtensisAccount,
  getExtensisSystems,
  getFieldNames,
  getFields,
  getGlobalSettings,
  getHasIdentityService,
  getIdentityEncryptedPassword,
  getIdentitySession,
  getIngestConfig,
  getIntl,
  getIsAdminServerRestarting,
  getIsCommunicatingWithDirectoryService,
  getIsDirectoryServiceMappingsDirty,
  getIsDirectoryServiceQueryPending,
  getIsEmailServerConfigPendingValidation,
  getIsFullAdmin,
  getIsLoggedIn,
  getIsMediaEngineTestPending,
  getIsPendingResponseFromBonfire,
  getIsRestarting,
  getIsSendingDiagnosticData,
  getIsServerPlatformWindows,
  getLastActivity,
  getLicensedFeatures,
  getLicenseMaintenanceEndDate,
  getLicenses,
  getLocale,
  getLoggingConfig,
  getMaxSeatCount,
  getMediaEngines,
  getMediaEngineTotalSeatCount,
  getMetadataDefinitions,
  getMetadataMappings,
  getNotifications,
  getNPServerStatus,
  getPorts,
  getReachoutMessages,
  getRoles,
  getRTIEnabled,
  getSeatCount,
  getSelectedDirectoryServiceNode,
  getSelectedField,
  getSelectedFieldCategory,
  getSelectedFieldsListItemName,
  getSelectedRole,
  getServerErrorStatuses,
  getServerFolderRoot,
  getServerFolders,
  getServerRunState,
  getServerStartupFailureReason,
  getServerStatus,
  getSites,
  getSmartKeywordsSettings,
  getSmartKeywordsRemainingCount,
  getTotalAssetCount,
  getUseConstrainedIngest,
  getUserCatalogInfo,
  getUsername,
  getUsers,
};



// WEBPACK FOOTER //
// ./src/state/selectors.js